import { Box, Flex } from '@chakra-ui/react';
import { RefObject, useRef } from 'react';
import { useSelector } from 'react-redux';
import useScrollSpy from 'react-use-scrollspy';
import { Creator } from '~/clientApi/creator.model';
import { EmailNotVerifiedNotification } from '~/components/EmailNotVerifiedNotification';
import Spinner from '~/components/Spinner';
import { SOCIAL_PLATFORMS } from '~/constants';
import { routes } from '~/core/router';
import useIsSharedPage from '~/hooks/useIsSharedPage';
import { selectCreator } from '~/selectors/creator';
import {
  CreatorBenchmarks,
  DefaultMediaPackType,
  IPdfImage,
  InstagramInsightType,
} from '~/types';

import {
  INSTAGRAM_SECTION_ID,
  OVERALL_REACH_SECTION_ID,
  TIKTOK_SECTION_ID,
  YOUTUBE_SECTION_ID,
} from './MediaPackDetailDefaultScreen.constants';
import { AboutYouAndMetricsSection } from './components/AboutYouAndMetricsSection';
import { FooterSection } from './components/FooterSection';
import { HeaderSection } from './components/HeaderSection';
import { InstagramSection } from './components/InstagramSection';
import { TagsSection } from './components/TagsSection';
import { TiktokSection } from './components/TiktokSection';
import { YoutubeSection } from './components/YoutubeSection';

interface Props {
  isLoading: boolean;
  mediaPack: DefaultMediaPackType;
  creatorBenchmarks: CreatorBenchmarks;
  creatorInsights: InstagramInsightType;
  onOpenAboutYou?: () => void;
  pdfImages: IPdfImage[];
  isSharePage?: boolean;
  creatorSlug?: string;
}

export const MediaPackDetailDefaultScreenComponent: React.FC<Props> = ({
  isLoading,
  mediaPack,
  creatorBenchmarks,
  creatorInsights,
  onOpenAboutYou,
  isSharePage = false,
  pdfImages,
  creatorSlug,
}) => {
  const creator = useSelector(selectCreator);
  const isSharedView = useIsSharedPage() || isSharePage;

  const { platforms = [] } = mediaPack ?? {};
  const hasInstagram = platforms.includes(SOCIAL_PLATFORMS.instagram);
  const hasYoutube = platforms.includes(SOCIAL_PLATFORMS.youtube);
  const hasTiktok = platforms.includes(SOCIAL_PLATFORMS.tiktok);

  const overallRef = useRef(null);
  const instagramRef = useRef(null);
  const tiktokRef = useRef(null);
  const youtubeRef = useRef(null);

  const availableSections = mediaPack?.platforms?.reduce((acc, platform) => {
    if (platform === SOCIAL_PLATFORMS.instagram) {
      acc.push(instagramRef);
    }

    if (platform === SOCIAL_PLATFORMS.tiktok) {
      acc.push(tiktokRef);
    }

    if (platform === SOCIAL_PLATFORMS.youtube) {
      acc.push(youtubeRef);
    }

    return acc;
  }, [] as RefObject<HTMLElement>[]);

  const sectionRefs = [overallRef, ...availableSections];

  const activeSection =
    useScrollSpy({
      sectionElementRefs: sectionRefs,
      offsetPx: -180,
    }) ?? 0;

  return (
    <>
      {/* Negate default layout content paddings */}
      <Box marginY="-spacing-400" marginX="-spacing-300">
        {isLoading && <Spinner centered aria-label="Loading data" />}
        {mediaPack && (
          <>
            <HeaderSection
              creatorBenchmarks={creatorBenchmarks}
              creatorInsights={creatorInsights}
              mediaPack={mediaPack}
              pdfImages={pdfImages}
              activeSection={activeSection}
              isSharedView={isSharedView}
              sectionsRefs={sectionRefs}
            />

            <Box id={OVERALL_REACH_SECTION_ID} ref={overallRef} marginTop={20}>
              <AboutYouAndMetricsSection
                mediaPack={mediaPack}
                creator={creator as Creator}
                creatorBenchmarks={creatorBenchmarks}
                onOpenAboutYou={onOpenAboutYou}
                isSharedView={isSharedView}
              />
              <TagsSection mediaPack={mediaPack} />
            </Box>

            <Flex
              marginTop={12}
              width="100%"
              direction="column"
              backgroundColor="neutral.50"
              paddingBottom={20}
            >
              <Box
                width="100%"
                maxWidth="1500px"
                paddingX={{
                  base: 8,
                  md: 20,
                  xl: 48,
                }}
                margin="auto"
                marginTop={12}
              >
                {hasInstagram && (
                  <Box
                    id={INSTAGRAM_SECTION_ID}
                    ref={instagramRef}
                    _notLast={{
                      marginBottom: 36,
                    }}
                  >
                    <InstagramSection
                      creatorBenchmarks={creatorBenchmarks}
                      creatorInsights={creatorInsights}
                      mediaPack={mediaPack}
                      isSharedView={isSharedView}
                    />
                  </Box>
                )}

                {hasTiktok && (
                  <Box
                    id={TIKTOK_SECTION_ID}
                    ref={tiktokRef}
                    _notLast={{
                      marginBottom: 36,
                    }}
                  >
                    <TiktokSection
                      creatorBenchmarks={creatorBenchmarks}
                      creatorInsights={creatorInsights}
                      mediaPack={mediaPack}
                      isSharedView={isSharedView}
                    />
                  </Box>
                )}

                {hasYoutube && (
                  <Box
                    id={YOUTUBE_SECTION_ID}
                    ref={youtubeRef}
                    _notLast={{
                      marginBottom: 36,
                    }}
                  >
                    <YoutubeSection
                      creatorBenchmarks={creatorBenchmarks}
                      mediaPack={mediaPack}
                      isSharedView={isSharedView}
                    />
                  </Box>
                )}
              </Box>

              <EmailNotVerifiedNotification />
            </Flex>
          </>
        )}
        {!isSharedView && (
          <FooterSection
            previewAndShareLink={
              creatorSlug
                ? // TODO add to routes
                  `/t/${creatorSlug}/media-packs/${mediaPack.id}/share`
                : routes.mediaPackShareView(mediaPack.id)
            }
            exportPdfData={{
              creator: creator as Creator,
              creatorBenchmarks,
              creatorInsights,
              mediaPack,
              pdfImages,
            }}
          />
        )}
      </Box>
    </>
  );
};
